import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { GenericResultResponse } from "../../../../../types/common";
import { getPosAccByIdResponse, PosAcc } from "../../../../../types/pos";
import callApi from "../../../../../utils/api-client";
import RestaurantPosForm from "../RestaurantPosForm/RestaurantPosForm";
import { IRestaurantPosFormData } from "../../../../../models/IRestaurantPosForm";
import { useDispatch } from "react-redux";
import { updateIsLoading } from "../../../../../reducerSlices/loaderSlice";
import successToast from "../../../../../assets/images/successToast.svg";
import errorToast from "../../../../../assets/images/errorToast.svg";
import { ToastNotification } from "../../../../../common/components/toastNotification/ToastNotification";
import { constants } from "../../../../../common/constants/constants";

function UpdateRestaurantPosDetails() {
  const { posId } = useParams<{ posId: string }>();
  const [formUpdateState, setFormUpdateState] = React.useState<"edit" | "view">(
    "view"
  );
  const [toastData, setToastData] = useState(constants.defaultToastData);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [posAccount, setPosAccount] = React.useState<
    IRestaurantPosFormData | undefined
  >(undefined);

  const getPosAccountDetailsById = async (id: string | undefined) => {
    try {
      dispatch(updateIsLoading(true));
      const response = await callApi<
        GenericResultResponse<getPosAccByIdResponse>
      >(
        `admin/pos/${id}`,
        {
          method: "GET",
        },
        "getPosAccountDetailsById_UpdateRestaurantPosDetails"
      );

      if (!response) {
        return undefined;
      }
      setFormUpdateState(response.data.pos_base_url ? "view" : "edit");
      setPosAccount({
        baseUrl: response.data.pos_base_url || "",
        apiKey: response.data.api_key || "",
        secretKey: response.data.secret_key || "",
        business: {
          id: response.data.business_setup_id || "",
          name: response.data.business_name || "",
        },
        businessSearchName: "",
      });
    } catch (error) {
      console.error("Error fetching POS account details:", error);
    } finally {
      dispatch(updateIsLoading(false));
    }
  };

  useEffect(() => {
    getPosAccountDetailsById(posId);
  }, []);

  const onSubmit = async (data: IRestaurantPosFormData) => {
    const body = {
      pos_base_url: data.baseUrl,
      api_key: data.apiKey,
      secret_key: data.secretKey,
    };
    try {
      dispatch(updateIsLoading(true));
      await callApi<GenericResultResponse<PosAcc>>(
        `admin/pos/${posId}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
        "updatePOSEntry_UpdateRestaurantPosDetails"
      );
      setToastData({
        message: "POS linked successfully.",
        status: "Success",
      });
      navigate("/admin/pos");
      return true;
    } catch (error) {
      console.error("Error saving configuration:", error);
      setToastData({
        message:
          error instanceof Error
            ? error.message
            : "Error saving configuration. Please try again.",
        status: "Fail",
      });
      return false;
    } finally {
      setTimeout(() => {
        setToastData(constants.defaultToastData);
      }, 10000);
      dispatch(updateIsLoading(false));
    }
  };

  const toggleEditView = () => {
    setFormUpdateState((prevState) => (prevState === "view" ? "edit" : "view"));
  };

  if (!posAccount) {
    return <></>;
  }

  return (
    <div style={{ padding: "20px" }}>
      <RestaurantPosForm
        onSubmit={onSubmit}
        initialData={posAccount}
        disableBusinessSelection
        actionButtonLabel={
          posAccount.baseUrl ? "Update POS Details" : "Connect POS"
        }
        isUpdateMode
        formUpdateState={formUpdateState}
        editBtnCallback={toggleEditView}
      />
      {toastData?.message && (
        <ToastNotification
          icon={toastData.status === "Success" ? successToast : errorToast}
          content={toastData?.message}
          position={"top-right"}
          autoClose={2000}
        />
      )}
    </div>
  );
}

export default UpdateRestaurantPosDetails;
